import React, { useState, useMemo, useEffect } from 'react';
import './MobilePositions.css';

function MobilePositions({ positions, selectedExchanges, liveFundingRates }) {
  const [filterExchange, setFilterExchange] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second for countdown timers
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Combine all positions from selected exchanges
  const allPositions = useMemo(() => {
    if (!positions) return [];
    
    const positionsList = [];
    const exchanges = ['binance', 'hyperliquid', 'lighter', 'extended'];
    
    exchanges.forEach(exchangeKey => {
      if (selectedExchanges[exchangeKey] && positions[exchangeKey]) {
        const exchangePositions = positions[exchangeKey].positions || [];
        exchangePositions.forEach(pos => {
          positionsList.push({
            ...pos,
            exchangeName: exchangeKey.charAt(0).toUpperCase() + exchangeKey.slice(1)
          });
        });
      }
    });

    return positionsList;
  }, [positions, selectedExchanges]);

  // Filter by exchange
  const filteredPositions = useMemo(() => {
    if (filterExchange === 'all') return allPositions;
    return allPositions.filter(p => p.exchange.toLowerCase() === filterExchange.toLowerCase());
  }, [allPositions, filterExchange]);

  // Format countdown from seconds to HH:MM:SS
  const formatCountdown = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Calculate ROE% (Return on Equity)
  const calculateROE = (unrealizedPnl, marginUsed) => {
    if (!marginUsed || marginUsed === 0) return null;
    return (unrealizedPnl / marginUsed) * 100;
  };

  // Get funding countdown from live rates or position data
  const getCountdown = (position) => {
    const rateKey = `${position.exchange}_${position.pair}`;
    const liveRate = liveFundingRates?.[rateKey];
    if (liveRate?.countdown_seconds !== undefined) {
      return formatCountdown(liveRate.countdown_seconds);
    }
    if (position.funding_rate_info?.countdown_seconds !== undefined) {
      return formatCountdown(position.funding_rate_info.countdown_seconds);
    }
    return '-';
  };

  const exchanges = [
    { key: 'all', label: 'All' },
    { key: 'binance', label: 'Binance' },
    { key: 'hyperliquid', label: 'Hyperliquid' },
    { key: 'lighter', label: 'Lighter' },
    { key: 'extended', label: 'Extended' }
  ];

  return (
    <div className="mobile-positions">
      <div className="positions-header">
        <h1 className="mobile-header">All Positions ({filteredPositions.length})</h1>
        <div className="filter-container">
          <button 
            className="filter-button"
            onClick={() => setShowFilterMenu(!showFilterMenu)}
          >
            Filter by Exchange
          </button>
          {showFilterMenu && (
            <div className="filter-menu">
              {exchanges.map(ex => (
                <button
                  key={ex.key}
                  className={`filter-option ${filterExchange === ex.key ? 'active' : ''}`}
                  onClick={() => {
                    setFilterExchange(ex.key);
                    setShowFilterMenu(false);
                  }}
                >
                  {ex.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="positions-list">
        {filteredPositions.length === 0 ? (
          <div className="no-positions">No positions found</div>
        ) : (
          filteredPositions.map((position, index) => {
            const roe = calculateROE(position.unrealized_pnl, position.margin_used);
            const countdown = getCountdown(position);

            return (
              <div key={`${position.exchange}-${position.pair}-${index}`} className="position-card">
                <div className="position-header">
                  <div className="position-symbol">{position.pair}</div>
                  <div className="position-exchange">{position.exchangeName}</div>
                </div>

                <div className={`side-badge ${position.side.toLowerCase()}`}>
                  <span className="side-bar" aria-hidden="true"></span>
                  <span>{position.side}</span>
                </div>

                <div className="position-metrics">
                  <div className="metric-row">
                    <span className="metric-label">U-PnL:</span>
                    <span className={`metric-value ${position.unrealized_pnl >= 0 ? 'positive' : 'negative'}`}>
                      {position.unrealized_pnl >= 0 ? '+' : ''}${position.unrealized_pnl.toFixed(2)}
                      {roe !== null && (
                        <span className="roe-percent">
                          {' '}({roe >= 0 ? '+' : ''}{roe.toFixed(2)}%)
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="metric-row">
                    <span className="metric-label">Qty:</span>
                    <span className="metric-value">{position.size?.toFixed(2) || '-'}</span>
                  </div>

                  <div className="metric-row">
                    <span className="metric-label">Notional:</span>
                    <span className="metric-value">
                      ${(position.notional || (position.size * position.mark_price) || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="metric-row">
                    <span className="metric-label">Liq. Price:</span>
                    <span className="metric-value">
                      {position.liquidation_price ? `$${position.liquidation_price.toFixed(2)}` : '-'}
                    </span>
                  </div>

                  <div className="metric-row">
                    <span className="metric-label">Funding Countdown:</span>
                    <span className="metric-value countdown">{countdown}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default MobilePositions;

